// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Settings → Assistant → Dashboards: the dashboards the user saved or pinned, to open,
// pin to Home or put away. They stay live: opening one shows fresh numbers.

import {SettingsRow} from '@app/features/app/components/dialogs/shared/SettingsRow';
import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {DashboardCompactNotice, SavedListSkeleton} from '@app/features/konvo_dashboards/components/DashboardNotice';
import Dashboards from '@app/features/konvo_dashboards/state/Dashboards';
import {formatTime} from '@app/features/konvo_dashboards/utils/DashboardFormat';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {ChartBarIcon, CornersOutIcon, PushPinIcon, PushPinSlashIcon, TrashIcon} from '@app/features/ui/icons/Icons';
import {ActionButton, IconButton} from '@app/features/ui/konvo_controls/KonvoControls';
import {PageNoticeButton} from '@app/features/ui/page_notice/PageNotice';
import type {DashboardSummary} from '@fluxer/schema/src/domains/konvo/AssistantDashboard';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';

export const KonvoAssistantDashboardsSection = observer(function KonvoAssistantDashboardsSection() {
	const {t} = useLingui();
	useEffect(() => {
		void Dashboards.loadSummaries();
	}, []);
	const saved = (Dashboards.summaries ?? []).filter((summary) => summary.saved || summary.pinned);
	return (
		<SettingsSection
			id="assistant-dashboards"
			title={<Trans>Dashboards</Trans>}
			description={
				<Trans>
					Charts and dashboards you saved from the assistant. Pin one to show it on Home; ask the assistant to change
					it, send it on a schedule or alert you about a number.
				</Trans>
			}
			defaultExpanded={false}
		>
			{saved.map((summary) => (
				<DashboardRow key={summary.id} summary={summary} />
			))}
			{Dashboards.summaries && saved.length === 0 && (
				<DashboardCompactNotice
					title={t({
						message: 'No saved dashboards yet',
						comment: 'Settings, Assistant dashboards: title when nothing is saved or pinned.',
					})}
					text={t({
						message: "Ask the assistant for a chart, then choose Save or Pin to Home from the card's menu.",
						comment: 'Settings, Assistant dashboards: how to save one, shown when nothing is saved.',
					})}
					flx="konvo-dashboards.settings.empty"
				/>
			)}
			{Dashboards.summariesError && (
				<DashboardCompactNotice
					tone="bad"
					title={t({
						message: 'Could not load your dashboards',
						comment: 'Settings, Assistant dashboards: title when the list could not be read.',
					})}
					text={Dashboards.summariesError}
					actions={
						<PageNoticeButton ink onClick={() => void Dashboards.loadSummaries()}>
							{t({message: 'Try again', comment: 'Settings, Assistant dashboards error: reads the list again.'})}
						</PageNoticeButton>
					}
					flx="konvo-dashboards.settings.error"
				/>
			)}
			{!Dashboards.summaries && !Dashboards.summariesError && <SavedListSkeleton />}
		</SettingsSection>
	);
});

const DashboardRow = observer(function DashboardRow({summary}: {summary: DashboardSummary}) {
	const {t, i18n} = useLingui();
	const [confirm, setConfirm] = useState(false);
	const guard = async (job: () => Promise<void>, done: string) => {
		try {
			await job();
			ToastCommands.success(done);
		} catch {
			ToastCommands.error(t`That did not work. Try again.`);
		}
	};
	const open = () =>
		void import('@app/features/konvo_dashboards/components/DashboardModals').then(({openDashboardFullscreen}) =>
			openDashboardFullscreen({target: {kind: 'dashboard', id: summary.id}, title: summary.title, inChat: false}),
		);
	return (
		<SettingsRow
			icon={<ChartBarIcon size={18} />}
			title={summary.title}
			description={
				summary.pinned
					? t`On Home · updated ${formatTime(i18n, summary.updated_at)}`
					: t`Saved · updated ${formatTime(i18n, summary.updated_at)}`
			}
			control={() =>
				confirm ? (
					<span style={{display: 'flex', gap: 6}}>
						<ActionButton small tone="quiet" onClick={() => setConfirm(false)}>
							{t`Keep`}
						</ActionButton>
						<ActionButton
							small
							tone="danger"
							onClick={() => void guard(() => Dashboards.remove(summary.id), t`Dashboard deleted.`)}
						>
							{t`Delete`}
						</ActionButton>
					</span>
				) : (
					<span style={{display: 'flex', gap: 2}}>
						<IconButton label={t`Open full screen`} icon={<CornersOutIcon size={17} />} onClick={open} />
						<IconButton
							label={summary.pinned ? t`Unpin from Home` : t`Pin to Home`}
							icon={summary.pinned ? <PushPinSlashIcon size={17} /> : <PushPinIcon size={17} />}
							onClick={() =>
								void guard(
									() => Dashboards.update(summary.id, {pinned: !summary.pinned}),
									summary.pinned ? t`Unpinned from Home.` : t`Pinned to Home.`,
								)
							}
						/>
						<IconButton label={t`Delete`} icon={<TrashIcon size={17} />} onClick={() => setConfirm(true)} />
					</span>
				)
			}
		/>
	);
});
